import get from "lodash/get";
import * as Sentry from "@sentry/react";
import React, { createContext, useContext, useEffect, useReducer, useState } from "react";
import TransferSource from "manifest-components/src/classes/transferSource";
import TransferDestination, { AccountType, DestinationType } from "manifest-components/src/classes/transferDestination";
import {
  AssetAllocationClass,
  AssetClassDataResponse,
  ReallocationChoice,
  ReallocationSurveyData,
} from "manifest-components/src/classes/reallocation";
import Transfer, { TransferStatusTypes } from "manifest-components/src/classes/transfer";

import { IRAAccountOpeningDataResponse } from "manifest-components/src/api/APIIraRecommendation";
import TransferInstruction, { NewTransferStatusType } from "manifest-components/src/classes/transferInstruction";
import Session from "../../utils/session";
import TransferReducer from "./transferReducer";
import { TransferState } from "./transferState";
import API from "../../utils/api";
import { route, RoutePaths, useTypedLocation, useTypedNavigate } from "../../routes/RoutePaths";
import { Actions } from "./transferActions";
import { AppContext } from "../App/appProvider";
import { testTransferInstructionData } from "./testData";

const fetchTransferError = "Unable to fetch transfer";

export const localAPI = {
  getDestinationDetails: API.transfer.getDestinationDetails,
  createOrUpdateDestination: API.transfer.createOrUpdateDestination,
  updateSourceDetails: API.transfer.updateSourceDetails,
  getTransfersInProgress: API.transfer.getTransfersInProgress,
  getTransferWithInstructionsByStatus: API.transferInstruction.getTransferWithInstructionsByStatus,
  getTransferById: API.transfer.getTransferById,
  getTransferInstructionById: API.transferInstruction.getTransferInstruction,
  connectDestinationToTransfer: API.transfer.connectDestinationToTransfer,
  createNewTransfer: API.transfer.createNewTransfer,
  getProviderMailingDetails: API.transferInstruction.getProviderMailingDetails,
  removeTransfer: API.transfer.removeTransfer,
  removeExistingDestination: API.transfer.removeExistingDestinations,
  initiateTransfer: API.transfer.initiateTransfer,
  getTransferOptions: API.transfer.getTransferOptions,
  getDestinationAccountsByType: API.transfer.getDestinationAccountsByType,
  getProvidersList: API.user.getProvidersList,
  uploadSignature: API.user.uploadSignature,
  getPlaidToken: API.user.getPlaidToken,
  getPlaidTokenForUpdate: API.user.getPlaidTokenForUpdate,
  updatePreferences: API.user.updatePreferences,
  getEarningsByTransferId: API.transfer.getEarningsByTransferId,
  createReallocation: API.transfer.createReallocation,
  updateReallocationChoice: API.transfer.updateReallocationChoice,
  deleteReallocationChoice: API.transfer.deleteReallocationData,
  getReallocationCalculation: API.transfer.getReallocationCalculation,
  updateIRAAccountOpeningData: API.iraRecommendation.updateIRAAccountOpeningData,
  getIRARecommendations: API.iraRecommendation.getIRARecommendation,
  updateFuturemoneyConsent: API.iraRecommendation.updateFuturemoneyConsent,
  getIRAAccountOpeningData: API.iraRecommendation.getAccountOpeningData,
  getUserTransferInstructions: API.transfer.getUserTransferInstructions,
};

export type TransferProviderValue = ReturnType<typeof createProviderValue>;

export type TransferProviderAPIValue = typeof localAPI;

const createProviderValue = ({ api = localAPI }) => {
  const navigate = useTypedNavigate();
  const location = useTypedLocation();
  const [state, dispatch] = useReducer(TransferReducer, TransferState);
  const { isTestingTransferStatus } = useContext(AppContext);
  const [iraAccountOpeningData, setIRAaccountOpeningData] = useState<Record<string, any>>({});
  const [iraAccountOpeningId, setIRAAccountOpeningId] = useState<string | number>("");
  const [userTransferInstructions, setUserTransferInstructions] = useState<TransferInstruction[]>([]);

  useEffect(() => {
    const container = document.getElementById("main-container");
    if (container) {
      container.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, [location.pathname]);

  async function getDestinationDetails(transferId: string) {
    try {
      dispatch({
        type: Actions.FETCHING_TRANSFER_DESTINATION,
        payload: true,
      });
      const transferDestination = await api.getDestinationDetails(Session.userId, transferId);
      dispatch({
        payload: transferDestination,
        type: Actions.UPDATE_TRANSFER_DESTINATION,
      });
      return transferDestination;
    } catch (error) {
      Sentry.captureMessage(error as string);
      throw new Error(`${error}`);
    }
  }

  async function createOrUpdateDestinationDetails(params: { transferId: string; destination: TransferDestination }) {
    const { transferId, destination } = params;
    try {
      dispatch({
        type: Actions.UPDATING_TRANSFER_DESTINATION,
        payload: true,
      });
      const transferDestination = await api.createOrUpdateDestination(Session.userId, transferId, destination);
      dispatch({
        payload: transferDestination,
        type: Actions.UPDATE_TRANSFER_DESTINATION,
      });
      return transferDestination;
    } catch (error) {
      Sentry.captureMessage(error as string);
      throw new Error(`${error}`);
    }
  }

  async function removeDestinationAccount(params: { transferId: string }) {
    const { transferId } = params;

    try {
      await api.removeExistingDestination({ transferId, userId: Session.userId });
    } catch (e) {
      Sentry.captureMessage("Unable to remove destination account");
    }
    return getTransfer(state.currentTransfer.transferId || Session.currentTransferId);
  }

  async function updateSourceDetails(transferId: string, source: TransferSource) {
    try {
      dispatch({
        type: Actions.UPDATING_TRANSFER_SOURCE,
        payload: true,
      });
      const response = await api.updateSourceDetails(Session.userId, transferId, source);
      const updatedTransfer = await api.getTransferById({ userId: Session.userId, transferId });
      dispatch({
        payload: updatedTransfer.sourceAccounts,
        type: Actions.UPDATE_TRANSFER_SOURCE,
      });
      return {
        source: response,
        transfer: updatedTransfer,
      };
    } catch (error) {
      Sentry.captureMessage(error as string);
      throw new Error(`${error}`);
    }
  }

  async function getInProgressTransfers() {
    dispatch({
      type: Actions.FETCHING_IN_PROGRESS_TRANSFERS,
    });
    try {
      const transfers = await api.getTransfersInProgress({
        userId: Session.userId,
      });
      dispatch({
        payload: transfers,
        type: Actions.GET_IN_PROGRESS_TRANSFERS,
      });
      const transferIdReceived = get(transfers[0], "transferId") || "";
      Session.currentTransferId = transferIdReceived;
      // Only try to get transfer step and details if there are in progress transfers
      if (transfers.length > 0) {
        await getTransfer(state.currentTransfer.transferId || Session.currentTransferId);
      }
      dispatch({
        payload: transferIdReceived,
        type: Actions.UPDATE_TRANSFER_ID,
      });
      return transfers;
    } catch (e) {
      Sentry.captureMessage(fetchTransferError);
    }
  }

  async function getInitiatedTransfers() {
    dispatch({
      type: Actions.FETCHING_INITIATED_TRANSFERS,
    });
    try {
      const transfers = await api.getTransferWithInstructionsByStatus({
        userId: Session.userId,
        transferStatus: [TransferStatusTypes.initiatedAtProvider, TransferStatusTypes.processing],
      });
      dispatch({
        payload: transfers,
        type: Actions.GET_INITIATED_TRANSFERS,
      });
    } catch (e) {
      Sentry.captureMessage(fetchTransferError);
    }
  }

  async function getQueuedTransfers() {
    dispatch({
      type: Actions.FETCHING_QUEUED_TRANSFERS,
    });
    try {
      const transfers = await api.getTransferWithInstructionsByStatus({
        userId: Session.userId,
        transferStatus: [TransferStatusTypes.inQueue],
      });
      dispatch({
        payload: transfers,
        type: Actions.GET_QUEUED_TRANSFERS,
      });
    } catch (e) {
      Sentry.captureMessage(fetchTransferError);
    }
  }

  const getTransferInstructionsWithUserInputNeeded = async () => {
    dispatch({
      type: Actions.FETCHING_TRANSFER_INSTRUCTIONS_WITH_USER_INPUT_NEEDED,
    });
    try {
      const transfers = await api.getTransferWithInstructionsByStatus({
        userId: Session.userId,
        transferStatus: [TransferStatusTypes.userInputNeeded],
      });
      dispatch({
        payload: transfers,
        type: Actions.GET_TRANSFER_INSTRUCTIONS_WITH_USER_INPUT_NEEDED,
      });
      return transfers;
    } catch (e) {
      Sentry.captureMessage(fetchTransferError);
    }
  };

  // Get COMPLETE transfers
  const getCompleteTransfers = async () => {
    dispatch({
      type: Actions.FETCHING_COMPLETE_TRANSFERS,
    });
    try {
      const transfers = await api.getTransferWithInstructionsByStatus({
        userId: Session.userId,
        transferStatus: [TransferStatusTypes.complete],
      });
      dispatch({
        payload: transfers,
        type: Actions.GET_COMPLETE_TRANSFERS,
      });
    } catch (e) {
      Sentry.captureMessage(fetchTransferError);
    }
  };

  // Get CANCELLED transfers
  const getCancelledTransfers = async () => {
    dispatch({
      type: Actions.FETCHING_CANCELLED_TRANSFERS,
    });
    try {
      const transfers = await api.getTransferWithInstructionsByStatus({
        userId: Session.userId,
        transferStatus: [TransferStatusTypes.cancelled],
      });
      dispatch({
        payload: transfers,
        type: Actions.GET_CANCELLED_TRANSFERS,
      });
    } catch (e) {
      Sentry.captureMessage(fetchTransferError);
    }
  };

  // Get Transfer instruction with WAIT FOR IRA OPENING
  const getWaitForIraOpenTransfer = async () => {
    dispatch({
      type: Actions.FETCHING_WAIT_FOR_IRA_TRANSFER,
    });
    try {
      const transfers = await api.getTransferWithInstructionsByStatus({
        userId: Session.userId,
        transferStatus: [TransferStatusTypes.waitForIRAOpening],
      });
      dispatch({
        payload: transfers,
        type: Actions.GET_WAIT_FOR_IRA_TRANSFER,
      });
    } catch (e) {
      Sentry.captureMessage(fetchTransferError);
    }
  };

  // GET_TRANSFERRED_DESTINATIONS
  async function getTransferredDestinations() {
    dispatch({
      type: Actions.FETCHING_TRANSFERRED_DESTINATIONS,
    });
    try {
      const destinations = await api.getDestinationAccountsByType({
        userId: Session.userId,
        destinationType: DestinationType.traditionalDestination,
      });
      dispatch({
        payload: destinations,
        type: Actions.GET_TRANSFERRED_DESTINATIONS,
      });
      return destinations;
    } catch (e) {
      Sentry.captureMessage("Unable to fetch transfer destination");
    }
  }

  // GET_ROTH_TRANSFERRED_DESTINATIONS
  async function getRothTransferredDestinations() {
    try {
      const destinations = await api.getDestinationAccountsByType({
        userId: Session.userId,
        destinationType: DestinationType.rothDestination,
        destinationAccountType: AccountType.ira,
      });
      dispatch({
        payload: destinations,
        type: Actions.GET_ROTH_TRANSFERRED_DESTINATIONS,
      });
    } catch (e) {
      Sentry.captureMessage("Unable to fetch transfer destination");
    }
  }

  async function getPlaidToken(accountType: AccountType) {
    return api.getPlaidToken(Session.userId, accountType);
  }

  async function getPlaidTokenForUpdate(params: { aggregatorLinkId: number }) {
    return api.getPlaidTokenForUpdate({
      ...params,
      userId: Session.userId,
    });
  }

  async function getProvidersList() {
    dispatch({
      type: Actions.FETCHING_PROVIDERS_LIST,
    });
    try {
      const providers = await api.getProvidersList();
      dispatch({
        payload: providers,
        type: Actions.GET_PROVIDERS_LIST,
      });
    } catch (e) {
      Sentry.captureMessage("Unable to get provider list");
    }
  }

  async function getTransferInstructionsByTransferId(params: { transferId: string }) {
    const { transferId } = params;
    return API.transferInstruction.getTransferInstructionsByTransferId({
      userId: Session.userId,
      transferId,
    });
  }

  // CONNECT DESTINATION TO TRANSFER
  async function connectDestinationToTransfer(params: { transferId: string; destination: TransferDestination }) {
    const { transferId, destination } = params;
    try {
      await api.connectDestinationToTransfer({
        destination,
        userId: Session.userId,
        transferId,
      });
      dispatch({
        type: Actions.CONNECT_DESTINATION_TO_TRANSFER,
      });
      return getTransfer(state.currentTransfer.transferId || Session.currentTransferId);
    } catch (e) {
      Sentry.captureMessage("Unable to add destination account");
    }
  }

  // Set loading
  const setTransferLoading = (val) => {
    dispatch({
      payload: val,
      type: Actions.SET_LOADING,
    });
  };

  async function createNewTransfer(redirect = true) {
    setTransferLoading(true);
    try {
      const transfer = await api.createNewTransfer(Session.userId);
      Session.currentTransferId = transfer.transferId;
      dispatch({
        payload: transfer,
        type: Actions.INITIATE_NEW_TRANSFER,
      });
      if (redirect) {
        navigate(route(RoutePaths.transferSteps), {
          state: {
            transferId: transfer.transferId,
          },
        });
      }
    } catch (e) {
      Sentry.captureMessage("Unable to create new transfer");
    }
  }

  async function getTransfer(idToFetchTransferFor) {
    if (idToFetchTransferFor === "-1") return;
    dispatch({
      payload: true,
      type: Actions.SET_LOADING,
    });
    dispatch({
      type: Actions.FETCHING_CURRENT_TRANSFER,
    });
    try {
      const transfer = await api.getTransferById({
        userId: Session.userId,
        transferId: idToFetchTransferFor,
      });
      dispatch({
        payload: {
          data: transfer,
        },
        type: Actions.SET_CURRENT_TRANSFER,
      });
      dispatch({
        payload: false,
        type: Actions.SET_LOADING,
      });
      return transfer;
    } catch (e) {
      Sentry.captureMessage(fetchTransferError);
    }
  }

  const fetchTransferById = async (transferId: string | number): Promise<Transfer> => {
    return await api.getTransferById({
      userId: Session.userId,
      transferId: transferId.toString(),
    });
  };

  // Show Transfer Screen
  const showAllTransfersScreen = () => {
    dispatch({
      type: Actions.SHOW_ALL_TRANSFERS,
    });
  };

  // Show Transfer Screen
  const showCurrentTransferScreen = () => {
    dispatch({
      type: Actions.SHOW_CURRENT_TRANSFER,
    });
  };

  const removeData = () => {
    dispatch({
      type: Actions.REMOVE_DATA,
    });
  };

  const removeSourceAccount = async (sourceAccountId: number | string) => {
    return API.transfer.removeSourceAccount({
      userId: Session.userId,
      transferId: state.currentTransfer.transferId,
      sourceAccountId,
    });
  };

  // Delete Current Transfer
  const removeTransfer = async () => {
    try {
      await api.removeTransfer({
        userId: Session.userId,
        transferId: state.currentTransfer.transferId,
      });
      removeData();
      Session.currentTransferId = "";
    } catch (e) {
      Sentry.captureMessage("Failed to remove transfer data");
    }
  };

  const removeCurrentTransfer = () =>
    dispatch({
      type: Actions.REMOVE_TRANSFER,
    });

  // Review transfer details
  async function initiateTransfer() {
    const transferId = state.currentTransfer.transferId || Session.currentTransferId;
    try {
      await api.initiateTransfer({
        userId: Session.userId,
        transferId,
      });
      // eslint-disable-next-line no-alert
      removeData();
      window.manifestTesting.lastInitiatedTransferId = parseInt(transferId);
      return api.getTransferById({
        userId: Session.userId,
        transferId: state.currentTransfer.transferId || Session.currentTransferId,
      });
    } catch (e) {
      Sentry.captureMessage("Unable to initiate transfer");
    }
  }

  async function uploadSignature(signature) {
    try {
      await api.uploadSignature({
        userId: Session.userId,
        signature,
      });
      return true;
    } catch (e) {
      Sentry.captureMessage(e as string);
      throw new Error("Signature Upload Failed");
    }
  }

  const getTransferOptions = async () => {
    try {
      dispatch({
        type: Actions.FETCHING_TRANSFER_OPTIONS,
      });
      const response = await api.getTransferOptions(Session.userId);
      dispatch({
        type: Actions.GET_TRANSFER_OPTIONS,
        payload: response,
      });
    } catch (e) {
      Sentry.captureMessage(e as string);
      throw new Error("Failed to fetch transfer options");
    }
  };

  const saveTransferOptions = async ({
    hasCurrentEmployerSponsoredAccount,
    hasIra,
    hasSixMonthsSavings,
    loanImportant,
    tradeIndividualSecurities,
    tradingFrequency,
    willHaveEmployerSponsoredAccountSoon,
    financialEducationLevel,
  }) => {
    try {
      await api.updatePreferences({
        userId: Session.userId,
        preferences: {
          hasCurrentEmployerSponsoredAccount,
          hasIra,
          hasSixMonthsSavings,
          loanImportant,
          tradingFrequency,
          tradeIndividualSecurities,
          willHaveEmployerSponsoredAccountSoon,
          financialEducationLevel,
        },
      });
      await getTransferOptions();
    } catch (e) {
      Sentry.captureMessage(e as string);
      throw new Error("Failed to save transfer options");
    }
  };

  async function getEarningsByTransferId(params: { transferId: number | string }) {
    return api.getEarningsByTransferId({ userId: Session.userId, transferId: params.transferId });
  }

  async function createReallocation(params: {
    reallocationFormData: ReallocationSurveyData;
    destinationAccountId: number | string;
  }): Promise<{ reallocationId: number; response: AssetClassDataResponse }> {
    // TODO: add error catching
    const response = await api.createReallocation(params);
    const res = await api.getReallocationCalculation({
      userId: Session.userIdentityId,
      reallocationId: response.reallocationId,
    });
    return {
      reallocationId: response.reallocationId,
      response: res,
    };
  }

  async function updateReallocationChoice(params: {
    choice: ReallocationChoice;
    reallocationDetails: null | AssetAllocationClass;
    transferId: number;
    destinationAccountId: string | number;
    reallocationId: number | null;
  }) {
    const { userId } = Session;
    // TODO: add error catching
    await api.updateReallocationChoice({ ...params, userId });
  }

  async function deleteReallocationChoice(reallocationId: number) {
    // TODO: add error catching
    await api.deleteReallocationChoice(reallocationId);
  }

  async function getReallocationDetails(reallocationId: number) {
    // TODO: add error catching
    return await api.getReallocationCalculation({
      userId: Session.userIdentityId,
      reallocationId,
    });
  }

  const updateIRAAccountOpeningData = async (data: Record<string, any>) => {
    if (state.currentTransfer.destinationAccount?.destinationAccountId) {
      const res = await api.updateIRAAccountOpeningData({
        userId: Session.userId,
        destinationAccountId: state.currentTransfer.destinationAccount?.destinationAccountId,
        data,
      });
      if (res.accountOpeningDataJson) {
        setIRAaccountOpeningData(JSON.parse(res.accountOpeningDataJson) || {});
        setIRAAccountOpeningId(res.iraAccountOpeningDataId);
      }
    } else {
      const destinationAccount = await getDestinationDetails(state.currentTransfer.transferId);
      const res = await api.updateIRAAccountOpeningData({
        userId: Session.userId,
        destinationAccountId: destinationAccount.destinationAccountId,
        data,
      });
      if (res.accountOpeningDataJson) {
        setIRAaccountOpeningData(JSON.parse(res.accountOpeningDataJson) || {});
        setIRAAccountOpeningId(res.iraAccountOpeningDataId);
      }
    }
  };

  const fetchIRARecommendations = async (data: Record<string, any>): Promise<Record<string, string> | undefined> => {
    try {
      return await api.getIRARecommendations({ userId: Session.userId, data });
    } catch (e) {
      return {};
    }
  };

  useEffect(() => {
    if (state.currentTransfer.destinationAccount) {
      const data = state.currentTransfer.destinationAccount.iraAccountOpeningData?.accountOpeningDataJson;
      try {
        if (typeof data === "string") {
          setIRAaccountOpeningData(JSON.parse(data));
        }
        if (state.currentTransfer.destinationAccount.iraAccountOpeningData?.iraAccountOpeningDataId) {
          setIRAAccountOpeningId(
            state.currentTransfer.destinationAccount.iraAccountOpeningData?.iraAccountOpeningDataId
          );
        }
      } catch {
        // do nothing
      }
    }
  }, [state.currentTransfer.destinationAccount]);

  const updateFutureMoneyConsent = async (transferId: string | undefined) => {
    let dataId = iraAccountOpeningId;
    if (!dataId) {
      dataId = state.currentTransfer?.destinationAccount?.iraAccountOpeningData?.iraAccountOpeningDataId || "";
    }
    if (!dataId && transferId) {
      const transfer = await getTransfer(transferId);
      dataId = transfer?.destinationAccount?.iraAccountOpeningData?.iraAccountOpeningDataId || "";
    }
    if (!dataId) return;
    await api.updateFuturemoneyConsent({ userId: Session.userId, iraAccountOpeningDataId: dataId });
  };

  const fetchIRAAccountOpeningStatus = async () => {
    if (!iraAccountOpeningId) return "PENDING";
    const res = await api.getIRAAccountOpeningData({
      userId: Session.userId,
      iraAccountOpeningDataId: iraAccountOpeningId,
    });
    return (res as IRAAccountOpeningDataResponse).status || "PENDING";
  };

  const getIRAAccountOpeningData = async (id): Promise<IRAAccountOpeningDataResponse> => {
    return await api.getIRAAccountOpeningData({
      userId: Session.userId,
      iraAccountOpeningDataId: id,
    });
  };

  const getUserTransferInstructions = async () => {
    const res = await API.transfer.getUserTransferInstructions({ userId: Session.userId });
    if (res) {
      setUserTransferInstructions(res);
    }
  };

  const updateIRAAccountOpeningStatus = async (transferId: string, status: string) => {
    const transferRes = await fetchTransferById(transferId);
    const transferInstructionRes = await getTransferInstructionsByTransferId({ transferId });
    const iraAccountOpeningDataId = transferRes.destinationAccount?.iraAccountOpeningData?.iraAccountOpeningDataId;
    const iraAccountOpeningDataInstructionIds = transferInstructionRes.map(
      (transferInstruction) => transferInstruction.destinations[0].iraAccountOpeningData?.iraAccountOpeningDataId
    );
    await Promise.all(
      [iraAccountOpeningDataId, ...iraAccountOpeningDataInstructionIds].map((id) =>
        API.transferInstruction.updateIRAAccountOpeningStatus({
          userId: Session.userId,
          id,
          status,
        })
      )
    );
  };

  const updateIRAAccountOpeningStatusByAccountId = async ({ id, status }: { id: string; status: string }) => {
    try {
      await API.transferInstruction.updateIRAAccountOpeningStatus({
        userId: Session.userId,
        id,
        status,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const updateTransferInstructionStatus = async ({
    id,
    status,
  }: {
    id: string | number;
    status: NewTransferStatusType;
  }) => {
    try {
      await API.transferInstruction.updateTransferInstructionStatusNew({
        userId: Session.userId,
        transferInstructionId: id,
        transferInstructionStatus: status,
      });
    } catch (e) {
      console.error(e);
    }
  };

  return {
    ...state,
    getDestinationDetails,
    createOrUpdateDestinationDetails,
    uploadSignature,
    getTransferInstructionsWithUserInputNeeded,
    getProvidersList,
    getCompleteTransfers,
    getCancelledTransfers,
    getPlaidToken,
    getPlaidTokenForUpdate,
    getRothTransferredDestinations,
    getInitiatedTransfers,
    getQueuedTransfers,
    getInProgressTransfers,
    getTransferOptions,
    saveTransferOptions,
    getTransferredDestinations,
    getTransfer,
    createNewTransfer,
    initiateTransfer,
    connectDestinationToTransfer,
    removeCurrentTransfer,
    removeData,
    removeTransfer,
    setTransferLoading,
    showAllTransfersScreen,
    showCurrentTransferScreen,
    updateSourceDetails,
    removeSourceAccount,
    removeDestinationAccount,
    getTransferInstructionsByTransferId,
    getEarningsByTransferId,
    createReallocation,
    deleteReallocationChoice,
    updateReallocationChoice,
    getReallocationDetails,
    updateIRAAccountOpeningData,
    fetchIRARecommendations,
    iraAccountOpeningData,
    iraAccountOpeningId,
    updateFutureMoneyConsent,
    setIRAaccountOpeningData,
    getWaitForIraOpenTransfer,
    fetchIRAAccountOpeningStatus,
    fetchTransferById,
    getUserTransferInstructions,
    userTransferInstructions: isTestingTransferStatus ? testTransferInstructionData : userTransferInstructions,
    updateIRAAccountOpeningStatus,
    getIRAAccountOpeningData,
    updateIRAAccountOpeningStatusByAccountId,
    updateTransferInstructionStatus,
  };
};

export const TransferContext = createContext({} as TransferProviderValue);

const TransferProvider = (props) => {
  return <TransferContext.Provider value={createProviderValue(props)}>{props.children}</TransferContext.Provider>;
};

export default TransferProvider;
